import React from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';

interface InviteModalProps {
  open: boolean;
  onClose: () => void;
  copyUrl: string;
  copyLabel: string;
  onSendInvites: (emails: string[]) => Promise<void>;
  title: string;
  /** Optional ISO date string — if provided, the QR tab shows an expiry label */
  qrExpiry?: string;
  /** Whether the current user has a Pro plan. Defaults to true (no gate). */
  isPro?: boolean;
  /** Called when a non-Pro user tries to open the QR tab */
  onUpgrade?: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Extracts email addresses from a pasted string.
 * Handles formats from Google, Outlook, Apple Mail, and plain lists:
 *   - "Name <email>" or "<email>" (angle-bracket format)
 *   - "email1, email2" or "email1; email2" (delimiter-separated)
 *   - Mixed combinations of the above
 */
function parseEmailsFromPaste(text: string): string[] {
  const found: string[] = [];

  // First: pull anything inside angle brackets — covers "Name <email>" format
  const angleRe = /<([^>\s]+@[^>]+)>/g;
  let m: RegExpExecArray | null;
  // eslint-disable-next-line no-cond-assign
  while ((m = angleRe.exec(text)) !== null) {
    const email = m[1].trim().toLowerCase();
    if (EMAIL_RE.test(email) && !found.includes(email)) found.push(email);
  }

  // If we found angle-bracket emails, that was a structured paste — use only those
  if (found.length > 0) return found;

  // Fallback: split on commas, semicolons, whitespace, newlines
  const tokens = text.split(/[,;\s\n\r\t]+/);
  for (const token of tokens) {
    const clean = token.trim().toLowerCase();
    if (EMAIL_RE.test(clean) && !found.includes(clean)) found.push(clean);
  }

  return found;
}

export const InviteModal: React.FC<InviteModalProps> = ({
  open,
  onClose,
  copyUrl,
  copyLabel,
  onSendInvites,
  title,
  qrExpiry,
  isPro = true,
  onUpgrade
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState<'email' | 'qr'>('email');
  const [presenting, setPresenting] = React.useState(false);
  const [emails, setEmails] = React.useState<string[]>([]);
  const [input, setInput] = React.useState('');
  const [inputError, setInputError] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const qrCanvasRef = React.useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    if (!open) {
      setEmails([]);
      setInput('');
      setInputError(false);
      setCopied(false);
      setSending(false);
      setActiveTab('email');
      setPresenting(false);
    }
  }, [open]);

  // Close fullscreen on Escape
  React.useEffect(() => {
    if (!presenting) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setPresenting(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [presenting]);

  const addEmail = (raw: string) => {
    const trimmed = raw.replace(/[,;]/g, '').trim().toLowerCase();
    if (!trimmed) return;
    if (!EMAIL_RE.test(trimmed)) {
      setInputError(true);
      return;
    }
    setEmails((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]));
    setInput('');
    setInputError(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      addEmail(input);
    }
    if (e.key === 'Backspace' && !input && emails.length) {
      setEmails((prev) => prev.slice(0, -1));
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    const parsed = parseEmailsFromPaste(pasted);
    if (parsed.length > 0) {
      e.preventDefault();
      setEmails((prev) => {
        const next = [...prev];
        for (const email of parsed) {
          if (!next.includes(email)) next.push(email);
        }
        return next;
      });
      setInput('');
      setInputError(false);
    }
    // Single email or unrecognised paste — let it land in the input normally
  };

  const copyLink = () => {
    navigator.clipboard.writeText(copyUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSend = async () => {
    if (!emails.length || sending) return;
    setSending(true);
    try {
      await onSendInvites(emails);
      onClose();
    } finally {
      setSending(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !sending) onClose();
  };

  const handleDownload = () => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = 'standby-qr.png';
    a.click();
  };

  const formatExpiry = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

  if (!open) return null;

  return (
    <>
      {/* Modal */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[3px] flex items-center justify-center"
        onClick={handleOverlayClick}
      >
        <div className="bg-white dark:bg-[#1C1C1F] rounded-[16px] max-w-[480px] w-full mx-[16px] p-[24px] shadow-[0_20px_60px_rgba(0,0,0,0.25)]">

          {/* Header */}
          <div className="flex items-center justify-between mb-[16px]">
            <h2 className="font-display text-[18px] font-bold text-base-black dark:text-white">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="w-[28px] h-[28px] rounded-full flex items-center justify-center text-[#818185] hover:bg-gray-400 hover:text-base-black transition-all"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-[6px] mb-[20px]">
            <button
              type="button"
              onClick={() => setActiveTab('email')}
              className={`flex-1 h-[34px] rounded-[32px] text-[13px] font-medium transition-all ${
                activeTab === 'email'
                  ? 'bg-[#1D1D1E] text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F]'
                  : 'bg-gray-400 dark:bg-[#28282B] text-gray-900 dark:text-[#A0A0A3] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] dark:hover:text-white'
              }`}
            >
              {t('inviteModal.emailInvite')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (!isPro && onUpgrade) { onUpgrade(); return; }
                setActiveTab('qr');
              }}
              className={`flex-1 h-[34px] rounded-[32px] text-[13px] font-medium transition-all flex items-center justify-center gap-[5px] ${
                activeTab === 'qr'
                  ? 'bg-[#1D1D1E] text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F]'
                  : 'bg-gray-400 dark:bg-[#28282B] text-gray-900 dark:text-[#A0A0A3] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] dark:hover:text-white'
              }`}
            >
              {!isPro && (
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
                  <rect x="3" y="7" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
                  <path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              )}
              {t('inviteModal.qrCode')}
            </button>
          </div>

          {activeTab === 'email' ? (
            <>
              {/* Copy link section */}
              <p className="font-body text-[11px] font-semibold text-gray-900 uppercase tracking-wider mb-[8px]">
                {copyLabel}
              </p>
              <div className="flex items-center gap-[8px] mb-[20px]">
                <div className="flex-1 h-[40px] rounded-[20px] border border-gray-500 bg-gray-400 px-[14px] flex items-center overflow-hidden min-w-0">
                  <span className="font-mono text-[11px] text-[#818185] truncate">{copyUrl}</span>
                </div>
                <button
                  type="button"
                  onClick={copyLink}
                  className="flex-shrink-0 h-[40px] px-[16px] rounded-[32px] bg-[#1D1D1E] text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all min-w-[80px] text-center"
                >
                  {copied ? t('common.copied') : t('common.copy')}
                </button>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-[12px] mb-[16px]">
                <div className="flex-1 h-px bg-[#E5E5ED]" />
                <span className="font-body text-[12px] text-gray-900 flex-shrink-0">{t('inviteModal.orSendInvite')}</span>
                <div className="flex-1 h-px bg-[#E5E5ED]" />
              </div>

              {/* Email input */}
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (inputError) setInputError(false);
                }}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onBlur={() => { if (input.trim()) addEmail(input); }}
                placeholder={t('inviteModal.emailPlaceholder')}
                className={`h-[44px] w-full rounded-[24px] border px-[16px] font-body text-[13px] text-base-black bg-white placeholder-[#BFBFC0] outline-none transition-colors ${
                  inputError
                    ? 'border-red-500'
                    : 'border-gray-500 hover:border-[#BCEBFB] focus:border-[#BCEBFB] focus:shadow-[0_0_0_4px_rgba(93,225,255,0.20)]'
                }`}
              />
              {inputError ? (
                <p className="font-body text-[12px] text-red-500 mt-[4px] ml-[4px]">{t('inviteModal.invalidEmail')}</p>
              ) : emails.length === 0 && (
                <p className="font-body text-[11px] text-[#818185] mt-[6px] ml-[4px]">{t('inviteModal.emailHint')}</p>
              )}

              {/* Email pills */}
              {emails.length > 0 && (
                <div className="flex flex-wrap gap-[6px] mt-[10px]">
                  {emails.map((email) => (
                    <span
                      key={email}
                      className="flex items-center gap-[4px] bg-[#F5F5F6] border border-[#E5E5ED] rounded-[20px] pl-[10px] pr-[6px] py-[3px] font-body text-[12px] text-base-black"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => setEmails((prev) => prev.filter((em) => em !== email))}
                        className="w-[14px] h-[14px] rounded-full flex items-center justify-center text-[#818185] hover:bg-[#E5E5ED] hover:text-base-black transition-all flex-shrink-0"
                      >
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <path d="M1 1l6 6M7 1L1 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Footer */}
              <div className="flex gap-[8px] mt-[16px]">
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={emails.length === 0 || sending}
                  className="flex-1 h-[44px] rounded-[32px] bg-[#1D1D1E] text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[14px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {sending
                    ? t('inviteModal.sending')
                    : emails.length > 0
                    ? t('inviteModal.sendCount', { count: emails.length })
                    : t('inviteModal.sendInvites')}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={sending}
                  className="flex-1 h-[44px] rounded-[32px] bg-gray-400 dark:bg-[#28282B] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] font-body text-[14px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all disabled:opacity-40"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </>
          ) : (
            /* QR tab */
            <div className="flex flex-col items-center">
              {/* QR code */}
              <div className="p-[16px] bg-white rounded-[12px] border border-[rgba(0,0,0,0.08)] shadow-sm mb-[16px]">
                <QRCodeCanvas
                  ref={qrCanvasRef}
                  value={copyUrl}
                  size={200}
                  marginSize={1}
                />
              </div>

              {/* URL label */}
              <p className="font-mono text-[11px] text-[#818185] truncate max-w-full px-[4px] mb-[8px] text-center">
                {copyUrl}
              </p>

              {/* Expiry badge */}
              {qrExpiry && (
                <span className="inline-flex items-center gap-[4px] px-[10px] py-[3px] rounded-[20px] bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E] font-body text-[12px] font-medium mb-[16px]">
                  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
                    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M8 4.5v3.75l2.25 2.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  {t('inviteModal.qrExpiresOn', { date: formatExpiry(qrExpiry) })}
                </span>
              )}

              {/* Action buttons */}
              <div className="flex gap-[8px] w-full">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="flex-1 h-[44px] rounded-[32px] bg-gray-400 dark:bg-[#28282B] border border-gray-500 dark:border-[rgba(255,255,255,0.08)] font-body text-[13px] font-medium text-base-black dark:text-[#E8E8EA] hover:bg-[#E0E0E3] dark:hover:bg-[#333336] transition-all flex items-center justify-center gap-[6px]"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M8 2v8M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  {t('inviteModal.downloadQr')}
                </button>
                <button
                  type="button"
                  onClick={() => setPresenting(true)}
                  className="flex-1 h-[44px] rounded-[32px] bg-[#1D1D1E] text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all flex items-center justify-center gap-[6px]"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <rect x="1" y="2" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M8 12v2M5 14h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  {t('inviteModal.presentQr')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen presenter overlay */}
      {presenting && (
        <div className="fixed inset-0 z-[60] bg-black flex flex-col items-center justify-center">
          <button
            type="button"
            onClick={() => setPresenting(false)}
            className="absolute top-[20px] right-[20px] w-[44px] h-[44px] rounded-full bg-white/10 hover:bg-white/20 transition-all flex items-center justify-center text-white"
            title="Close (Esc)"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          <div className="p-[24px] bg-white rounded-[20px] shadow-[0_20px_80px_rgba(0,0,0,0.6)] mb-[24px]">
            <QRCodeSVG
              value={copyUrl}
              size={340}
              marginSize={1}
            />
          </div>

          <p className="font-mono text-[13px] text-white/50 truncate max-w-[420px] px-[16px] text-center">
            {copyUrl}
          </p>

          {qrExpiry && (
            <span className="mt-[12px] inline-flex items-center gap-[4px] px-[12px] py-[4px] rounded-[20px] bg-[#FFFBEB]/10 border border-[#FDE68A]/30 text-[#FDE68A] font-body text-[12px]">
              <svg width="11" height="11" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 4.5v3.75l2.25 2.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              {t('inviteModal.qrExpiresOn', { date: formatExpiry(qrExpiry) })}
            </span>
          )}

          <p className="mt-[16px] font-body text-[12px] text-white/30">{t('inviteModal.pressEscToClose')}</p>
        </div>
      )}
    </>
  );
};
